import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format } from "date-fns";
import { it } from "date-fns/locale";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatFileSize(bytes: number): string {
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(1)} ${units[unitIndex]}`;
}

export function generateFileName(baseName: string = "file") {
  const timestamp = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 14); // YYYYMMDDHHMMSS
  return `${baseName}_${timestamp}.xlsx`;
}

export const mimeMap: { [key: string]: string } = {
  ".pdf": "application/pdf",
  ".xls": "application/vnd.ms-excel",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".csv": "text/csv",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
  ".doc": "application/msword",
  ".docx":
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".ppt": "application/vnd.ms-powerpoint",
  ".pptx":
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
};

export function getFileType(contentType: string): string {
  // Mappatura dei tipi di contenuto in categorie
  const contentTypeMapping: { [key: string]: string } = {
    "image/png": "img",
    "image/jpeg": "img",
    "image/jpg": "img",
    "image/gif": "img",
    "image/webp": "img",
    "application/pdf": "pdf",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "doc",
    "application/vnd.ms-excel": "excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      "excel",
  };

  // Restituisce il tipo mappato o "unknown" se non è presente nella mappa
  return contentTypeMapping[contentType] || "unknown";
}

export function getStatusColor(status: string): string {
  switch (status) {
    case "draft":
      return "bg-muted-foreground";
    case "inprogress":
      return "bg-blue-500";
    case "completed":
      return "bg-green-500";
    case "expired":
      return "bg-red-500";
    default:
      return "bg-muted-foreground";
  }
}

interface FileMetadata {
  fileName: string;
}

export function convertJsonToFile(jsonString: string): File {
  // Parso la stringa JSON
  const fileData: FileMetadata = JSON.parse(jsonString);

  // Creo un Blob vuoto per simulare il contenuto
  const emptyBlob = new Blob([], { type: "application/octet-stream" });

  // Creo l'oggetto File
  const file = new File([emptyBlob], fileData.fileName, {
    type: "application/vnd.openxmlformats-officedocument.presentationml.presentation", // pptx MIME type
    lastModified: Date.now(),
  });

  return file;
}

export function getDeadlineStatus(deadlineDate: Date | string | undefined) {
  if (!deadlineDate) return { status: "none", daysRemaining: null };

  const deadline = new Date(deadlineDate);
  const today = new Date();

  // Reset time parts for accurate day calculation
  deadline.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const diffTime = deadline.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return { status: "overdue", daysRemaining: diffDays }; // Past deadline
  } else if (diffDays <= 7) {
    return { status: "approaching", daysRemaining: diffDays }; // Within a week
  } else {
    return { status: "normal", daysRemaining: diffDays }; // More than a week
  }
}

export function getDeadlineColor(status: string) {
  switch (status) {
    case "overdue":
      return "bg-destructive text-destructive-foreground";
    case "approaching":
      return "bg-yellow-500/80 text-yellow-950";
    default:
      return "bg-muted text-muted-foreground";
  }
}

/**
 * Format a number as Euro currency with consistent formatting
 * regardless of server or client environment
 */
export function formatEuro(amount: number): string {
  // Ensure we have a number to work with
  const safeAmount = typeof amount === "number" ? amount : 0;

  // Use a static, non-locale specific approach to ensure consistency
  const parts = safeAmount.toFixed(2).split(".");
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const decimalPart = parts[1] || "00";

  // Format as "X.XXX,XX €" (Italian style)
  return `${integerPart},${decimalPart} €`;
}

export function formatDate(date: Date): string {
  try {
    return format(date, "dd/MM/yyyy", { locale: it });
  } catch (e) {
    return "Data non valida";
  }
}
